import { SaleProduct_Type } from "gen/txn/models_pb"
import { ChevronDown, Link } from "lucide-react"
import { Chip } from "shared-web/components/base/informational/Chip"
import { ChipColor } from "shared-web/components/base/informational/Chip/Chip"
import { FloatingTooltip } from "shared-web/components/base/overlay/FloatingTooltip"
import { Icon, IconSizes } from "shared-web/components/Icon"

import { sale as saleURL } from "urls/sales"

import { SaleLink } from "../types/SaleForm.types"

const COLLAPSE_AT = 2

const LinkIcon: React.FC = () => (
  <Icon
    icon={Link}
    size={IconSizes.ExtraSmall}
    className="color-icon-default"
    aria-hidden="true"
  />
)

interface LinkedChipProps {
  saleLink: SaleLink
  color: ChipColor
}

const LinkedChip: React.FC<LinkedChipProps> = ({ saleLink, color }) => (
  <Chip
    onClick={(e) => e.stopPropagation()}
    color={color}
    text={saleLink.identifier}
    href={saleURL(saleLink.id, saleLink.saleStatus)}
  />
)

interface CollapsedTooltipChipProps {
  links: SaleLink[]
  chipColor: ChipColor
  collapsedText: string
}

const CollapsedTooltipChip: React.FC<CollapsedTooltipChipProps> = ({
  links,
  chipColor,
  collapsedText,
}) => (
  <div className="text-v2-foreground-default-weak flex items-center">
    <FloatingTooltip
      content={
        <div className="flex flex-col gap-y-2">
          {links.map((link) => (
            <div key={link.id.toString()}>
              <LinkedChip saleLink={link} color={chipColor} />
            </div>
          ))}
        </div>
      }
      className="p-2"
      placement="bottom"
      variant="complex"
      trigger={
        <Chip color={chipColor} text={collapsedText} IconInput={ChevronDown} />
      }
    />
  </div>
)

interface ReturnLinksDisplayProps {
  links: SaleLink[]
  type: SaleProduct_Type
}

export const ReturnLinksDisplay: React.FC<ReturnLinksDisplayProps> = ({
  links,
  type,
}) => {
  if (type === SaleProduct_Type.UNLINKED_RETURN) {
    return (
      <div className="text-v2-foreground-default-weak flex flex-row items-center gap-x-1">
        <Chip color="yellow" text="Return" />
        <LinkIcon />
        <Chip color="gray" text="Unlinked" />
      </div>
    )
  }

  if (links.length === 0) {
    return null
  }

  if (links.length >= COLLAPSE_AT) {
    return (
      <CollapsedTooltipChip
        links={links}
        chipColor="yellow"
        collapsedText={`${links.length} returns`}
      />
    )
  }

  const headerLabel =
    type === SaleProduct_Type.RETURN ? "Original sale" : "Returned in"
  return (
    <div className="text-v2-foreground-default-weak flex flex-row items-center gap-x-1">
      <Chip color="yellow" text={headerLabel} />
      <LinkIcon />
      {links.map((link) => (
        <LinkedChip key={link.id.toString()} saleLink={link} color="yellow" />
      ))}
    </div>
  )
}

interface CloseOutLinksDisplayProps {
  links: SaleLink[]
}

export const CloseOutLinksDisplay: React.FC<CloseOutLinksDisplayProps> = ({
  links,
}) => {
  if (links.length === 0) {
    return null
  }

  if (links.length >= COLLAPSE_AT) {
    return (
      <CollapsedTooltipChip
        links={links}
        chipColor="green"
        collapsedText={`${links.length} close outs`}
      />
    )
  }

  return (
    <div className="text-v2-foreground-default-weak flex flex-col items-start gap-y-1">
      {links.map((link) => (
        <LinkedChip key={link.id.toString()} saleLink={link} color="green" />
      ))}
    </div>
  )
}
